import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, message, Row, Typography } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useGetOrganization } from "../../../../../api/v1";
import { leaseService } from "../../../../../configs/constants";
import { useGetPaymentTypeOptions } from "../../../../../hooks/useGetPaymentTypeOptions";
import type { CommercialBillingScheduleBreakdownItem, PaymentSchedule } from "../../../../../types/bookings";
import { AppButton } from "../../../../atoms/Button";
import { Popup, PopupConfirm } from "../../../../atoms/Popup";
import {
  convertToDayjs,
  DecimalUtils,
  extractValue,
  findSelectOption,
  formatDateByType,
} from "../../../../libs/utils";
import { usePendingRequestsActions } from "../../../PendingApprovalRequets/api/useRefreshPendingRequests";
import { CommonCard } from "../../components";
import {
  getBookingDetail,
  GetBookingDetailOptions,
  setBookingForm,
  setFormState,
  updateBooking,
} from "../../ducks/actions";
import { useBookingSelector } from "../../hooks/useBookingSelector";
import { useTorRules } from "../../hooks/useTorRules";
import { hasTorRules } from "../../utils";
import AdditionalPayments from "../AdditionalPayments";
import RejectPopup from "../modalContent/rejectPopup";
import { buildBookingStepPayload } from "../tenant-details/utils";
import {
  amount_type,
  ApprovalHeader,
  buildDepositPaymentsPayload,
  filterDropdownValue,
  payment_modes,
  PendingHeader,
} from "../utils";
import type { BreakdownMode, PlanFieldItem } from "./add-plan";
import {
  AddPlanAccordion,
  createEmptyCommercialPlanField,
  BaseRentBreakdownModeToggle,
  mapFormPlansToCommercialPaymentPlan,
  PAYMENT_PLAN_TOR_TYPE_KEY,
  shouldResetPaymentPlansForTorTypeChange,
  useResetPaymentPlansOnTorTypeChange,
  useSaveBillingScheduleForPlans,
} from "./add-plan";
import { BreakdownStaleProvider } from "./add-plan/BreakdownStaleContext";
import { buildCommercialBillingScheduleBreakdownPayload } from "./add-plan/buildCommercialBillingScheduleBreakdownPayload";
import {
  buildHydratedCommercialPlans,
  createTurnoverRentOnlySeedPlan,
  resolveCommercialPlansSource,
} from "./add-plan/hydrateCommercialPlans";
import { useBreakdownStaleRegistry } from "./add-plan/useBreakdownStaleRegistry";
import AddPlanComm, { rentTypeOptions } from "./AddPlanComm";
import AddTorRules from "./AddTorRules";
import type { TorRulesFormValues } from "./tor-rules";
import { PreviewScheduleGrid } from "./preview-schedule";

const { Text } = Typography;

const initcharge = {
  itemId: null,
  type: "",
  intallments: { label: 1, value: 1 },
  mode: {
    label: "Cheque",
    value: "cheque",
  },
  amount: 0,
  total: 0,
};

const PaymentPlansComm = (props) => {
  const { parking } = props;
  const { Title } = Typography;
  const [billingType, setBillingType] = useState([]);
  const [modalvisible, setModalvisible] = useState(false);
  const [modalTrigger, setModalTrigger] = useState(0);
  const [savedTorRules, setSavedTorRules] = useState<TorRulesFormValues | null>(null);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule[]>();
  const [bookingPayments, setBookingPayments] = useState<any[]>([]);
  const [resetBookingPaymentEditTrigger, setResetBookingPaymentEditTrigger] = useState(0);
  const dispatch: any = useDispatch();
  const { refreshPendingRequests } = usePendingRequestsActions();
  const { organization } = useGetOrganization();
  const { bookingForm, defaultBookingPaymentsAtCreateTime } = useSelector((state: any) => state.newBooking);
  const bookingDetails = useBookingSelector();
  const { isTurnoverRentOnly, isBaseRentOnly, torType } = useTorRules();
  const [selectedBreakdownMode, setSelectedBreakdownMode] = useState<BreakdownMode>("billingSchedule");

  const isSeparateInvoicingEnabled = Boolean(organization?.monthlyBillingBreakdownAndTorSeparateInvoicing);

  let isPaymentPlanApprovalEnable =
    bookingDetails?.paymentPlanApproval && bookingDetails?.paymentPlanApproval !== "disable";
  // redux states

  const { paymentTypesOptions } = useGetPaymentTypeOptions({
    usageType: bookingDetails?.propertyDetails?.usagesType,
    configKey: "masterPaymentTypeAttributesWithVat",
  });

  const yearDifference =
    moment(bookingForm?.leaseTerms?.leaseEndDate).diff(moment(bookingForm?.leaseTerms?.leaseStartDate), "years") + 1;
  const isleaseTypeTor = bookingForm?.torType
    ? hasTorRules(bookingForm?.torType)
    : hasTorRules(bookingDetails?.torType);

  let unitArea = bookingDetails?.propertyDetails?.areaSqft;
  const { leaseId = bookingDetails.leaseId } = useParams();

  const { handleSaveBillingSchedule } = useSaveBillingScheduleForPlans({ leaseId });

  const form = useForm({
    defaultValues: {
      plans: [],
      othercharges: [],
      bookingPayments: [],
    },
  });

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    clearErrors,
    reset,
    register,
  } = form;

  const {
    fields: otherChargesFields,
    append: otherChargesAppend,
    remove: otherChargesRemove,
  } = useFieldArray({
    control,
    name: "othercharges",
  });

  const {
    fields: plansFields,
    append: plansAppend,
    remove: plansRemove,
    replace: plansReplace,
  } = useFieldArray({
    control,
    name: "plans",
  });

  const handleRemovePlan = useCallback(
    (index: number) => {
      plansRemove(index);
      const remainingPlans = getValues("plans") ?? [];
      dispatch(
        setBookingForm({
          ...bookingForm,
          commercialPaymentPlan: mapFormPlansToCommercialPaymentPlan(remainingPlans),
          paymentPlan: mapFormPlansToCommercialPaymentPlan(remainingPlans),
        }),
      );
    },
    [bookingForm, dispatch, getValues, plansRemove],
  );

  const { resetKey: paymentPlanResetKey } = useResetPaymentPlansOnTorTypeChange({
    torType,
    isTurnoverRentOnly,
    paymentPlanTorType: bookingForm?.[PAYMENT_PLAN_TOR_TYPE_KEY],
    bookingForm,
    dispatch,
    replace: plansReplace,
    clearErrors: () => clearErrors("plans"),
  });

  const hasPaymentPlan = plansFields.length > 0 || otherChargesFields.length > 0;
  const isVisiblePreviewScheduleButton = hasPaymentPlan;
  const isVisibleBaseRentBreakdownMode = hasPaymentPlan && isBaseRentOnly && isSeparateInvoicingEnabled;

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const plansWatch = useWatch({ control, name: "plans" });

  const totalAnnualRent = useMemo(() => {
    if (!plansWatch?.length) return null;
    const amounts = plansWatch.map((plan) => plan?.annualRent ?? 0);
    return DecimalUtils.toNumber(DecimalUtils.add(amounts));
  }, [plansWatch]);

  const {
    registerPaymentPlanBreakdownStale,
    registerServiceChargeBreakdownStale,
    assertNoStaleCommercialBreakdowns,
    showSendForApproval,
  } = useBreakdownStaleRegistry({
    plansFields,
    isSeparateInvoicingEnabled,
    plansWatch,
    savedPaymentPlan: bookingDetails?.paymentPlan,
    savedCommercialBreakdown: bookingDetails?.commercialBillingScheduleBreakdown,
    isDirty,
    yearDifference,
    isTurnoverRentOnly,
    requiresTorPeriod: isleaseTypeTor,
    paymentPlanApproval: bookingDetails?.paymentPlanApproval,
  });

  const getNextBookingPaymentRowId = () =>
    crypto?.randomUUID?.() || `booking-payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const mapPreviewPaymentSchedule = (schedule: PaymentSchedule[] = []): PaymentSchedule[] => {
    return schedule.map((item: PaymentSchedule) => ({
      installments: item?.installments,
      description: item?.description,
      actualAmount: item?.actualAmount,
      vat: item?.vat,
      vatInPercentage: item?.vatInPercentage,
      totalAmount: item?.totalAmount,
      dueDate: item?.dueDate,
      invoiceDate: item?.invoiceDate,
      isWriteOff: item?.isWriteOff,
      yearNumber: item?.yearNumber,
    }));
  };

  useEffect(() => {
    setPaymentSchedule(mapPreviewPaymentSchedule(bookingDetails?.paymentSchedule || []));
  }, [bookingDetails?.paymentSchedule]);

  useEffect(() => {
    if (bookingForm?.bookingTerms?.billingCharges?.length > 0) {
      const billingLines = [];
      bookingForm?.bookingTerms?.billingCharges?.forEach((item) => {
        const type = filterDropdownValue(item["billingChargesType"], billingType);
        const obj = {
          billingChargesType: { ...type, isDisabled: true },
          intallments: {
            label: item?.numberOfInstallments || 1,
            value: item?.numberOfInstallments || 1,
          },
          invoiceDate: convertToDayjs(item?.invoiceDate),
          dueDate: convertToDayjs(item?.dueDate),
          modeOfPayment: filterDropdownValue(item?.modeOfPayment || "cheque", payment_modes),
          actualAmount: item?.amountType === "areaSqft" ? item?.unitPerSqAmount : item?.actualAmount,
          vat: item?.vat,
          amountType: filterDropdownValue(item?.amountType, amount_type),
          total: item?.total,
        };
        if (type) billingLines.push(obj);
      });

      if (billingLines.length > 0) setValue("othercharges", billingLines);
    }
  }, [bookingForm, billingType, setValue]);

  useEffect(() => {
    const normalizePayments = (payments: any[] = []) =>
      payments.map((payment) => ({
        ...payment,
        __rowId: payment?.__rowId || getNextBookingPaymentRowId(),
      }));

    setBookingPayments(normalizePayments(bookingForm?.bookingPayments || defaultBookingPaymentsAtCreateTime || []));
  }, [bookingForm, defaultBookingPaymentsAtCreateTime]);

  const sanitizedDepositPayments = useMemo(() => {
    return buildDepositPaymentsPayload(bookingPayments);
  }, [bookingPayments]);

  useEffect(() => {
    if (!paymentTypesOptions.length) return;

    const disabledValues = new Set(["rent_installment", "security_deposit"]);

    setBillingType(
      paymentTypesOptions.map((item) => ({
        label: item.label,
        value: item.value,
        vatInPercentage: item.vatInPercentage,
        isDisabled: disabledValues.has(item.value),
      })),
    );
  }, [paymentTypesOptions]);

  useEffect(() => {
    if (shouldResetPaymentPlansForTorTypeChange(bookingForm?.[PAYMENT_PLAN_TOR_TYPE_KEY], torType)) {
      return;
    }

    const bookingPlans = bookingForm?.commercialPaymentPlan || bookingForm?.paymentPlan || [];
    const breakdownData: CommercialBillingScheduleBreakdownItem[] =
      bookingDetails?.commercialBillingScheduleBreakdown ?? [];

    // Prefer breakdown years when API has more breakdown rows than payment-plan rows (common for TOR-only).
    const plansSource = resolveCommercialPlansSource({
      bookingPlans,
      breakdownData,
      isTurnoverRentOnly,
    });

    if (plansSource.length > 0) {
      const currentPlans = getValues("plans") ?? [];
      if (currentPlans.length > 0 && currentPlans.length < bookingPlans.length) {
        return;
      }

      const finalPlans = buildHydratedCommercialPlans({
        plansSource,
        breakdownData,
        isTurnoverRentOnly,
        fallbackUnitAreaSqft: bookingDetails?.propertyDetails?.areaSqft,
      });

      if (JSON.stringify(currentPlans) !== JSON.stringify(finalPlans)) {
        setValue("plans", finalPlans, { shouldDirty: false, shouldValidate: false });
      }

      if (bookingForm?.[PAYMENT_PLAN_TOR_TYPE_KEY] == null && torType) {
        dispatch(setBookingForm({ [PAYMENT_PLAN_TOR_TYPE_KEY]: torType }));
      }
    }
  }, [bookingForm, bookingDetails, dispatch, getValues, isTurnoverRentOnly, setValue, torType]);

  useEffect(() => {
    if (!isTurnoverRentOnly) {
      const existingBookingPlans = bookingForm?.commercialPaymentPlan || bookingForm?.paymentPlan;
      if (!existingBookingPlans?.length) setValue("plans", []);
      return;
    }

    const currentPlans = getValues("plans") || [];
    if (currentPlans.length > 0) return;

    setValue("plans", [createTurnoverRentOnlySeedPlan()], {
      shouldDirty: false,
      shouldValidate: false,
    });

    if (bookingForm?.[PAYMENT_PLAN_TOR_TYPE_KEY] == null && torType) {
      dispatch(setBookingForm({ [PAYMENT_PLAN_TOR_TYPE_KEY]: torType }));
    }
  }, [isTurnoverRentOnly, bookingForm, dispatch, getValues, setValue, torType]);

  const validateForm = async () => {
    const [plansValid, otherChargesValid] = await Promise.all([
      trigger("plans", { shouldFocus: true }),
      trigger("othercharges", { shouldFocus: true }),
    ]);

    return plansValid && otherChargesValid;
  };

  const transformBillingCharges = (charges: any[]): unknown[] => {
    return (charges ?? []).map((item) => {
      const amountType = item?.amountType?.value;
      const actualAmountRaw = item?.actualAmount;

      const isAreaBased = amountType === "areaSqft";
      const isTotalVariesByPlan = item?.total === "Varies by plan";
      const actualAmount = isAreaBased
        ? DecimalUtils.toNumber(DecimalUtils.multiply(actualAmountRaw, unitArea))
        : actualAmountRaw;

      return {
        billingChargesType: item?.billingChargesType?.value,
        modeOfPayment: item?.modeOfPayment?.value,
        invoiceDate: formatDateByType(item?.invoiceDate || item?.dueDate, "api"),
        dueDate: formatDateByType(item?.dueDate, "api"),
        numberOfInstallments: item?.intallments?.value || 1,
        amountType,
        ...(isAreaBased ? { unitPerSqAmount: DecimalUtils.toNumber(actualAmountRaw) } : {}),
        actualAmount: DecimalUtils.toNumber(actualAmount),
        vat: DecimalUtils.toNumber(item?.vat),
        ...(!isTotalVariesByPlan ? { total: DecimalUtils.toNumber(item?.total) } : {}),
      };
    });
  };

  const prepareFormData = (data?: any, type?: string) => {
    let dataCopy = { ...(data || getValues()) };
    const selectedPlans = (dataCopy?.plans ?? []).map((item) => ({
      ...item,
      installments: item?.installments?.value,
      rentType: item?.rentType?.value,
      torPeriod: item?.torPeriod?.value,
    }));

    if (selectedPlans.length === 0 && type !== "sendPlan") {
      message.info("Please add at least one payment plan to proceed");
      return;
    }

    return {
      commercialPaymentPlan: selectedPlans,
      bookingTerms: {
        ...bookingForm?.bookingTerms,
        billingCharges: dataCopy?.othercharges?.length > 0 ? transformBillingCharges(dataCopy?.othercharges) : [],
      },
      depositPayments: sanitizedDepositPayments,
    };
  };

  const onSubmit = async (data?: any, type?) => {
    setResetBookingPaymentEditTrigger((prev) => prev + 1);
    const isValid = await validateForm();
    if (!isValid) return;

    if (!assertNoStaleCommercialBreakdowns()) return;

    let body = prepareFormData(data, type);
    if (!body) return;

    const hasTorRulesConfigured =
      (bookingDetails?.torRules?.length ?? 0) > 0 || (savedTorRules?.torRules?.length ?? 0) > 0;

    if (isleaseTypeTor && hasTorRulesConfigured && type !== "sendPlan") {
      body["torType"] = bookingForm?.torType;
    } else if (isleaseTypeTor && !hasTorRulesConfigured && type !== "sendPlan") {
      dispatch(setBookingForm({ ...bookingForm, ...body }));
      message.info("Please add at least one TOR rule to proceed");
      return;
    }

    let valid = true;
    if (!(await trigger("othercharges", { shouldFocus: true }))) valid = false;
    if (!(await trigger("plans", { shouldFocus: true }))) valid = false;

    if (!valid) return;

    body = buildBookingStepPayload(bookingForm, bookingDetails, body);

    props.parentState({ load: true });

    try {
      if (Boolean(organization?.monthlyBillingBreakdownAndTorSeparateInvoicing)) {
        const plans: PlanFieldItem[] = getValues("plans");
        const otherCharges = getValues("othercharges");

        const response = await handleSaveBillingSchedule({
          plans,
          billingCharges: otherCharges?.length > 0 ? transformBillingCharges(otherCharges) : [],
          depositPayments: sanitizedDepositPayments,
        });
        if (response?.success) message.success(response?.message);
      } else {
        const response = await dispatch(updateBooking({ leaseId, body }));
        message.success(response?.data?.message);
      }

      if (type !== "sendPlan") await dispatch(getBookingDetail(leaseId));

      if (type === "next") {
        dispatch(setFormState());
        dispatch(setBookingForm(body));
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || error.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  const sendPaymentPlan = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    if (!assertNoStaleCommercialBreakdowns()) return;

    const addPayments = getValues("plans");

    PopupConfirm({
      title: "Are you sure you want to send plans for approval?",
      content: "",
      onOk: async () => {
        if (addPayments?.length > 0) {
          const finalPayments = addPayments
            .filter((p) => isTurnoverRentOnly || (p?.annualRent != null && p?.annualRent > 0))
            .map((plan) => ({
              ...plan,
              rentType: findSelectOption(rentTypeOptions, plan.rentType?.value),
            }));

          sendPlanApi(
            [...finalPayments],
            [...(bookingDetails?.paymentPlan?.length > 0 ? bookingDetails?.paymentPlan : [])],
          );
        }
      },
      okText: "Confirm",
    });
  };

  const sendPlanApi = async (newPlans, oldPlans) => {
    await onSubmit(null, "sendPlan"); //added fix for bug: 8194
    props.parentState({ load: true });
    const finalData = newPlans?.map((item) => {
      let old = oldPlans.find((plan) => plan.noOfYear === item.noOfYear);

      const installments = extractValue(item?.installments);
      const rentType = extractValue(item?.rentType);
      const torPeriod = extractValue(item?.torPeriod);
      const annualrent = DecimalUtils.toNumber(item?.annualRent);

      item = {
        noOfYear: item?.noOfYear,
        installments: installments,
        rentType: rentType,
        unitAreaInSqft: item?.unitAreaInSqft,
        rentValue: item?.rentValue,
        annualRent: annualrent,
        securityDeposit: item?.securityDeposit,

        oldInstallments: old?.installments || installments,
        oldRentType: old?.rentType || rentType,
        oldUnitAreaInSqft: old?.unitAreaInSqft || item?.unitAreaInSqft,
        oldRentValue: old?.rentValue || item?.rentValue,
        oldAnnualRent: old?.annualRent || annualrent,
        oldSecurityDeposit: old?.securityDeposit || item?.securityDeposit,

        ...(isSeparateInvoicingEnabled && isleaseTypeTor
          ? { torPeriod: torPeriod, oldTorPeriod: old?.torPeriod || torPeriod }
          : {}),
      };
      return item;
    });
    let body: Record<string, unknown> = {
      paymentPlanSendForApproval: "yes",
      paymentPlan: finalData,
      depositPayments: sanitizedDepositPayments,
    };

    if (isSeparateInvoicingEnabled) {
      const commercialPayload = buildCommercialBillingScheduleBreakdownPayload(getValues("plans") ?? []);
      body.billingBreakdown = JSON.stringify(commercialPayload);
    }

    try {
      const config: any = {
        data: body,
        method: "PATCH",
        url: `${leaseService}/booking/${leaseId}`,
      };

      const response = await axios(config);
      if (isPaymentPlanApprovalEnable) await refreshPendingRequests();
      message.success(response.data.message);
      await dispatch(getBookingDetail(leaseId));
      reset(null, { keepValues: true });
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  const approvePlan = async () => {
    let body = {
      paymentPlanSendForApproval: "approved",
      depositPayments: sanitizedDepositPayments,
    };
    PopupConfirm({
      title: "Are you sure you want to approve payment plans?",
      content: "",
      onOk: async () => approvalPlanApi(body),
      okText: "Confirm",
    });
  };

  const rejectPlan = async (data) => {
    let body = {
      paymentPlanSendForApproval: "rejected",
      rejectionReason: data?.comments ?? "",
      depositPayments: sanitizedDepositPayments,
    };
    setModalvisible(false);
    await approvalPlanApi(body);
  };

  const approvalPlanApi = async (body) => {
    props.parentState({ load: true });
    try {
      const config: any = {
        data: body,
        method: "PATCH",
        url: `${leaseService}/booking/${leaseId}`,
      };
      const response = await axios(config);
      isPaymentPlanApprovalEnable && (await refreshPendingRequests());

      message.success(response.data.message);
      await dispatch(getBookingDetail(leaseId));
      reset(null, { keepValues: true });
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  const saveDraftPlansToRedux = useCallback(() => {
    const currentPlans: PlanFieldItem[] = getValues("plans") ?? [];
    if (currentPlans.length === 0) return;

    dispatch(
      setBookingForm({
        ...bookingForm,
        commercialPaymentPlan: mapFormPlansToCommercialPaymentPlan(currentPlans),
        ...(torType ? { [PAYMENT_PLAN_TOR_TYPE_KEY]: torType } : {}),
      }),
    );
  }, [bookingForm, dispatch, getValues, torType]);

  const addTorRules = () => {
    saveDraftPlansToRedux();
    setModalTrigger(1);
    setModalvisible(true);
  };

  const generatePaymentSchedule = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    if (!assertNoStaleCommercialBreakdowns()) return;

    const formData = prepareFormData();
    if (!formData) return;

    const body = buildBookingStepPayload(bookingForm, bookingDetails, formData);

    props.parentState({ load: true });
    setPaymentSchedule([]);
    try {
      if (Boolean(organization?.monthlyBillingBreakdownAndTorSeparateInvoicing)) {
        const plans: PlanFieldItem[] = getValues("plans");
        const otherCharges = getValues("othercharges");

        await handleSaveBillingSchedule({
          plans,
          billingCharges: otherCharges?.length > 0 ? transformBillingCharges(otherCharges) : [],
          depositPayments: sanitizedDepositPayments,
        });
      } else {
        await dispatch(updateBooking({ leaseId, body }));
      }
      const options: GetBookingDetailOptions = isSeparateInvoicingEnabled ? { params: { previewSchedule: "yes" } } : {};
      await dispatch(getBookingDetail(leaseId, options));

      setModalvisible(true);
      setModalTrigger(2);
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || error.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  const popup = [
    {
      title: (
        <Title level={3} className="mb-0">
          Reject Reason
        </Title>
      ),
      width: 650,
      content: <RejectPopup handleReject={rejectPlan} onClose={() => setModalvisible(false)} />,
    },
    {
      title: (
        <Title level={3} className="mb-0">
          {bookingDetails?.torRules?.length > 0 ? "Edit TOR Rules" : "Add TOR Rules"}
        </Title>
      ),
      style: { padding: 0 },
      width: 850,
      content: <AddTorRules setTorRules={setSavedTorRules} onClose={() => setModalvisible(false)} />,
    },
    {
      width: "75rem",
      style: { padding: 0 },
      title: (
        <Title level={3} className="mb-0">
          Preview Schedule
        </Title>
      ),
      content: <PreviewScheduleGrid paymentSchedule={paymentSchedule} />,
    },
  ];

  const popupProps = {
    closable: true,
    visibility: modalvisible,
    title: popup[modalTrigger]?.title,
    content: popup[modalTrigger].content,
    width: popup[modalTrigger].width,
    style: popup[modalTrigger].style,
    onCancel: () => setModalvisible(false),
  };

  const topActions = (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
      {isVisibleBaseRentBreakdownMode && (
        <BaseRentBreakdownModeToggle value={selectedBreakdownMode} onChange={setSelectedBreakdownMode} />
      )}
      {showSendForApproval && <AppButton onClick={sendPaymentPlan}>Send for Approval</AppButton>}
      {isVisiblePreviewScheduleButton && <AppButton onClick={generatePaymentSchedule}>Preview Schedule</AppButton>}
    </div>
  );

  const statusHeader = () => {
    // managersManager leaseManager landlord
    if (bookingDetails?.paymentPlanApproval === "sent" && bookingDetails?.loginUserType === "leaseManager") {
      return <PendingHeader />;
    } else if (
      bookingDetails?.paymentPlanApproval === "sent" &&
      (bookingDetails?.loginUserType === "managersManager" || bookingDetails?.loginUserType === "landlord")
    ) {
      return (
        <ApprovalHeader
          approve={approvePlan}
          reject={() => {
            setModalTrigger(0);
            setModalvisible(true);
          }}
        />
      );
    } else return null;
  };

  return (
    <FormProvider {...form}>
      <Form
        layout="vertical"
        name="bookingform"
        className="bf-main"
        scrollToFirstError
        onFinish={handleSubmit(onSubmit)}
      >
        <CommonCard
          label="Payment Plans"
          leftBtnLabel="Back"
          rightBtnLabel="Next"
          handleNext={
            ((!isDirty && bookingDetails?.paymentPlanApproval === "approved") ||
              bookingDetails?.paymentPlanApproval === "disable") &&
            onSubmit
          }
          handleBack={() => dispatch(setFormState("back"))}
          torActions={
            isleaseTypeTor && {
              action: addTorRules,
              label: "Add TOR Rules",
            }
          }
          topActions={topActions}
          statusHeader={statusHeader()}
          parentState={props.parentState}
        >
          <Col span={24}>
            <Row gutter={24}>
              <Col span={24}>
                {isSeparateInvoicingEnabled ? (
                  <BreakdownStaleProvider
                    registerPaymentPlanBreakdownStale={registerPaymentPlanBreakdownStale}
                    registerServiceChargeBreakdownStale={registerServiceChargeBreakdownStale}
                    assertNoStaleCommercialBreakdowns={assertNoStaleCommercialBreakdowns}
                  >
                    <AddPlanAccordion
                      key={paymentPlanResetKey}
                      resetKey={paymentPlanResetKey}
                      fields={plansFields}
                      remove={handleRemovePlan}
                      selectedBreakdownMode={selectedBreakdownMode}
                    />
                  </BreakdownStaleProvider>
                ) : (
                  <AddPlanComm key={paymentPlanResetKey} fields={plansFields} remove={handleRemovePlan} />
                )}
              </Col>
              {bookingDetails?.paymentPlanApproval !== "sent" && yearDifference > plansFields.length && (
                <Col span={24}>
                  <Button
                    type="dashed"
                    onClick={() => plansAppend(createEmptyCommercialPlanField(isTurnoverRentOnly))}
                    htmlType="button"
                    icon={<PlusOutlined />}
                    block={true}
                  >
                    <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
                      Add Payment Plans
                    </Text>
                  </Button>
                </Col>
              )}
              <Col span={24} style={{ marginTop: "20px" }}>
                <p className="ft-main-label">Additional Payments</p>

                <AdditionalPayments
                  fields={otherChargesFields}
                  billingType={billingType}
                  control={control}
                  errors={errors}
                  disableTypeModeDueDate={true}
                  remove={otherChargesRemove}
                  setValue={setValue}
                  disableAll={props.disableAll}
                  getValues={getValues}
                  register={register}
                  // WIP just for set flow later will change
                  unitArea={unitArea || 0}
                  parking={parking}
                  totalAnnualRent={totalAnnualRent}
                  depositPayments={bookingDetails?.depositPayments}
                  bookingPayments={bookingPayments}
                  onDeleteBookingPayment={(index) => {
                    const updatedFormPayments = (getValues("bookingPayments") || []).filter(
                      (_, currentIndex) => currentIndex !== index,
                    );
                    setValue("bookingPayments", updatedFormPayments, { shouldDirty: false, shouldValidate: false });
                    setBookingPayments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
                  }}
                  onUpdateBookingPayment={(index, updatedPayment) =>
                    setBookingPayments((prev) =>
                      prev.map((payment, currentIndex) =>
                        currentIndex === index
                          ? {
                              ...updatedPayment,
                              __rowId: payment?.__rowId || updatedPayment?.__rowId || getNextBookingPaymentRowId(),
                            }
                          : payment,
                      ),
                    )
                  }
                  resetBookingPaymentEditTrigger={resetBookingPaymentEditTrigger}
                  dueData={bookingForm?.leaseTerms?.leaseStartDate}
                />

                {!props.disableAll && (
                  <Row gutter={24}>
                    <Col span={24}>
                      <Button
                        type="dashed"
                        // className="p-0"
                        htmlType="button"
                        icon={<PlusOutlined />}
                        block={true}
                        onClick={() => otherChargesAppend(initcharge)}
                      >
                        <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
                          Add Another Payment
                        </Text>
                      </Button>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Col>
        </CommonCard>
      </Form>
      <Popup destroyOnClose={true} {...popupProps} />
    </FormProvider>
  );
};

export default PaymentPlansComm;
